/**
 * Fixed Asset React Hooks
 * Epic 66: Tárgyi Eszköz Nyilvántartás & ÉCS
 *
 * TanStack Query hooks for fixed assets and depreciation.
 */

import {
  createFixedAsset,
  getDepreciationReport,
  getFixedAsset,
  getFullyDepreciatedAssets,
  listFixedAssets,
  runDepreciation,
  type DepreciationReport,
  type FixedAsset,
  type FixedAssetQueryResult,
} from '@/api/fixed-asset';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useFixedAssets(params?: { category?: string; status?: string }) {
  const query = useQuery({
    queryKey: ['fixed-assets', 'list', params],
    queryFn: async () => {
      const res = await listFixedAssets(params);
      return res.data;
    },
  });

  return {
    result: query.data as FixedAssetQueryResult | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useFixedAssetDetail(id: string | undefined) {
  const query = useQuery({
    queryKey: ['fixed-assets', 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getFixedAsset(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    asset: query.data as FixedAsset | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

export function useCreateFixedAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      category: string;
      purchasePrice: number;
      commissionDate: string;
      inventoryNumber?: string;
      productId?: string;
      warehouseId?: string;
      serialNumber?: string;
      note?: string;
    }) => createFixedAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fixed-assets'] });
    },
  });
}

export function useFullyDepreciatedAssets() {
  const query = useQuery({
    queryKey: ['fixed-assets', 'fully-depreciated'],
    queryFn: async () => {
      const res = await getFullyDepreciatedAssets();
      return res.data;
    },
  });

  return {
    assets: (query.data ?? []) as FixedAsset[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

export function useDepreciationReport(params: {
  year: number;
  month?: number;
  quarter?: number;
  category?: string;
}) {
  const query = useQuery({
    queryKey: ['fixed-assets', 'depreciation-report', params],
    queryFn: async () => {
      const res = await getDepreciationReport(params);
      return res.data;
    },
  });

  return {
    report: query.data as DepreciationReport | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

export function useRunDepreciation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { year: number; month: number }) => runDepreciation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fixed-assets'] });
    },
  });
}
